import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Save, Check, ClipboardList, Target, BarChart3, TrendingUp, Users, Award, Sparkles, Mail, Info, Send, Loader2 } from 'lucide-react';
import { getCurrentWeekActivity, getWeekActivity, autosaveActivity, submitActivityReport, getWeeklyDefaults, sendDailyReport } from '../utils/api';
import { getCurrentWeek, getPreviousWeek, getNextWeek, formatDateDisplay, formatDayWithDate, isCurrentWeek, calculateTotals } from '../utils/weekUtils';
import { toast } from 'sonner';
import WeekCalendar from '../components/WeekCalendar';
import AgentLayout from '../components/AgentLayout';
import SendReportModal from '../components/SendReportModal';
import ReportInfoBlock from '../components/ReportInfoBlock';

const AgentDashboard = () => {
  const navigate = useNavigate();
  const [agentInfo, setAgentInfo] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState('');
  const [currentWeekEnd, setCurrentWeekEnd] = useState('');
  const [activityData, setActivityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [totals, setTotals] = useState(null);
  const [saveTimeout, setSaveTimeout] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(true);
  const [dailySentDates, setDailySentDates] = useState([]);
  const [sendingDay, setSendingDay] = useState(null);
  const [reportMeta, setReportMeta] = useState({ weeklyApGoal: '', areasWorked: '' });

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) {
      navigate('/agent/login');
      return;
    }
    setAgentInfo(JSON.parse(info));
    loadCurrentWeek(token);

    // Check if first login - show welcome
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, [navigate]);

  const isBlankWeek = (activities) => activities.every(day =>
    !day.offDay && !day.startTime && !day.endTime &&
    (day.leadsWorked || 0) === 0 && (day.doorKnocks || 0) === 0 &&
    (day.phoneCalls || 0) === 0 && (day.presentations || 0) === 0 &&
    (day.sales || 0) === 0 && (day.futureAppointments || 0) === 0 &&
    (day.annualizedPremium || 0) === 0
  );

  const applyDefaultsToActivity = async (activity, tkn) => {
    if (activity.status === 'submitted' || !isBlankWeek(activity.activities)) return activity;
    try {
      const res = await getWeeklyDefaults(tkn);
      if (res.activityDefaults && res.activityDefaults.length === 7) {
        const updated = activity.activities.map((day, i) => {
          const def = res.activityDefaults[i];
          return {
            ...day,
            offDay: def.offDay || false,
            startTime: def.offDay ? '' : (def.startTime || ''),
            endTime: def.offDay ? '' : (def.endTime || ''),
            leadsWorked: def.offDay ? 0 : (def.leadsWorked || 0),
            doorKnocks: def.offDay ? 0 : (def.doorKnocks || 0),
            phoneCalls: def.offDay ? 0 : (def.phoneCalls || 0),
            presentations: def.offDay ? 0 : (def.presentations || 0),
            futureAppointments: def.offDay ? 0 : (def.futureAppointments || 0),
          };
        });
        return { ...activity, activities: updated };
      }
    } catch (e) { /* defaults not set */ }
    return activity;
  };

  const loadCurrentWeek = async (token) => {
    try {
      setIsLoading(true);
      const result = await getCurrentWeekActivity(token);
      if (result.success) {
        const activity = await applyDefaultsToActivity(result.activity, token);
        setActivityData(activity);
        setDailySentDates(activity.dailySentDates || []);
        setReportMeta(activity.reportMeta || { weeklyApGoal: '', areasWorked: '' });
        setCurrentWeekStart(activity.weekStartDate);
        setCurrentWeekEnd(activity.weekEndDate);
        setTotals(calculateTotals(activity.activities));
      }
    } catch (error) {
      console.error('Error loading current week:', error);
      toast.error('Failed to load current week data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadWeek = async (weekStart, token) => {
    try {
      setIsLoading(true);
      const result = await getWeekActivity(weekStart, token);
      if (result.success) {
        const activity = await applyDefaultsToActivity(result.activity, token);
        setActivityData(activity);
        setDailySentDates(activity.dailySentDates || []);
        setReportMeta(activity.reportMeta || { weeklyApGoal: '', areasWorked: '' });
        setCurrentWeekStart(activity.weekStartDate);
        setCurrentWeekEnd(activity.weekEndDate);
        setTotals(calculateTotals(activity.activities));
      }
    } catch (error) {
      console.error('Error loading week:', error);
      toast.error('Failed to load week data');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousWeek = () => {
    const token = localStorage.getItem('agentToken');
    const prevWeek = getPreviousWeek(currentWeekStart);
    loadWeek(prevWeek.start, token);
  };

  const handleNextWeek = () => {
    const token = localStorage.getItem('agentToken');
    const nextWeek = getNextWeek(currentWeekStart);
    loadWeek(nextWeek.start, token);
  };

  const handleThisWeek = () => {
    const token = localStorage.getItem('agentToken');
    loadCurrentWeek(token);
  };

  const handleCalendarWeekSelect = (start, end) => {
    const token = localStorage.getItem('agentToken');
    loadWeek(start, token);
  };

  const triggerAutosave = useCallback(() => {
    if (saveTimeout) clearTimeout(saveTimeout);
    const newTimeout = setTimeout(async () => {
      if (activityData) {
        await performAutosave();
      }
    }, 1000);
    setSaveTimeout(newTimeout);
  }, [activityData, saveTimeout]);

  const performAutosave = async () => {
    if (!activityData) return;
    const token = localStorage.getItem('agentToken');
    setIsSaving(true);
    try {
      const result = await autosaveActivity({
        weekStartDate: activityData.weekStartDate,
        weekEndDate: activityData.weekEndDate,
        activities: activityData.activities,
        notes: activityData.notes,
        reportMeta: reportMeta,
      }, token);
      if (result.success) {
        setLastSaved(new Date());
        setTotals(calculateTotals(activityData.activities));
      }
    } catch (error) {
      console.error('Autosave error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const updateActivity = (dayIndex, field, value) => {
    const newActivities = [...activityData.activities];
    if (field === 'offDay') {
      if (value) {
        newActivities[dayIndex] = {
          ...newActivities[dayIndex],
          offDay: true, startTime: '', endTime: '',
          leadsWorked: 0, doorKnocks: 0, phoneCalls: 0,
          presentations: 0, sales: 0, futureAppointments: 0,
          annualizedPremium: 0, referralsReceived: 0, referralsContacted: 0
        };
      } else {
        newActivities[dayIndex] = { ...newActivities[dayIndex], offDay: false };
      }
    } else {
      newActivities[dayIndex] = { ...newActivities[dayIndex], [field]: value };
    }
    setActivityData({ ...activityData, activities: newActivities });
    triggerAutosave();
  };

  const handleSubmit = async () => {
    if (!activityData) return;
    const hasActiveDay = activityData.activities.some(day => !day.offDay);
    if (!hasActiveDay) {
      toast.error('At least one active day is required to submit');
      return;
    }
    const token = localStorage.getItem('agentToken');
    try {
      const result = await submitActivityReport({
        weekStartDate: activityData.weekStartDate,
        weekEndDate: activityData.weekEndDate,
        activities: activityData.activities,
        notes: activityData.notes
      }, token);
      if (result.success) {
        toast.success('Activity report submitted!');
        setActivityData({ ...activityData, status: 'submitted' });
        
        // Show celebration on first ever submit
        const hasSubmitted = localStorage.getItem('hasSubmittedReport');
        if (!hasSubmitted) {
          setShowCelebration(true);
          localStorage.setItem('hasSubmittedReport', 'true');
          setTimeout(() => setShowCelebration(false), 4000);
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit report');
    }
  };

  const handleUnlock = () => {}; // kept for compatibility, no longer locks

  const dayHasData = (day) => {
    if (day.offDay) return false;
    return !!(day.startTime || day.endTime || day.leadsWorked > 0 || day.doorKnocks > 0 ||
      day.phoneCalls > 0 || day.presentations > 0 || day.sales > 0 ||
      day.futureAppointments > 0 || day.annualizedPremium > 0 ||
      day.referralsReceived > 0 || day.referralsContacted > 0);
  };

  const handleSendDailyReport = async (dayIndex) => {
    const token = localStorage.getItem('agentToken');
    const day = activityData.activities[dayIndex];
    setSendingDay(dayIndex);
    try {
      const result = await sendDailyReport({ weekStart: currentWeekStart, dayData: day }, token);
      if (result.success) {
        toast.success(`${day.dayOfWeek} report sent to your trainer!`);
        setDailySentDates(prev => [...prev, day.date]);
      } else {
        toast.error(result.message || 'Failed to send daily report');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send daily report');
    } finally {
      setSendingDay(null);
    }
  };

  if (isLoading || !activityData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const isThisWeek = isCurrentWeek(currentWeekStart);
  const isSubmitted = activityData.status === 'submitted';

  return (
    <AgentLayout title="Weekly Activity">
      <div data-testid="agent-dashboard">

      {/* Welcome / Onboarding Modal */}
      {showWelcome && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center backdrop-blur-sm" data-testid="welcome-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg mx-4 overflow-hidden">
            {onboardingStep === 0 && (
              <div className="text-center p-8">
                <div className="w-20 h-20 bg-gradient-to-br from-brand-blue to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Sparkles size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-brand-blue mb-2">Welcome, {agentInfo?.fullName}!</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  You're all set up in the Purposefully Covered Agent Portal. Let us give you a quick tour of your tools.
                </p>
                <div className="flex gap-3 justify-center">
                  <button onClick={() => setOnboardingStep(1)} className="bg-brand-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition" data-testid="welcome-tour-btn">
                    Show Me Around
                  </button>
                  <button onClick={() => { setShowWelcome(false); localStorage.setItem('hasSeenWelcome', 'true'); }} className="bg-gray-100 text-gray-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition" data-testid="welcome-skip-btn">
                    Skip Tour
                  </button>
                </div>
              </div>
            )}
            {onboardingStep >= 1 && onboardingStep <= 5 && (() => {
              const steps = [
                { icon: ClipboardList, color: 'bg-blue-500', title: 'Weekly Activity', desc: 'Track your daily activities here: leads worked, door knocks, phone calls, presentations, and sales. Data auto-saves as you type.' },
                { icon: Target, color: 'bg-brand-orange', title: 'Weekly Goals', desc: 'Set weekly performance goals and compare them against your actual results to stay on track.' },
                { icon: BarChart3, color: 'bg-green-600', title: 'Reports', desc: 'View your submitted reports by month. Print or export any week for your records.' },
                { icon: TrendingUp, color: 'bg-purple-600', title: 'Performance', desc: 'See KPI trends over time, identify patterns, and track your week-over-week growth.' },
                { icon: Users, color: 'bg-teal-600', title: 'Leads', desc: 'Manage your lead pipeline: create, track, assign, and convert leads into clients.' },
              ];
              const step = steps[onboardingStep - 1];
              const Icon = step.icon;
              return (
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs font-semibold text-gray-400 uppercase">Step {onboardingStep} of 5</span>
                    <button onClick={() => { setShowWelcome(false); localStorage.setItem('hasSeenWelcome', 'true'); }} className="text-gray-400 hover:text-gray-600 text-sm">Skip</button>
                  </div>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`${step.color} text-white w-14 h-14 rounded-xl flex items-center justify-center shadow`}>
                      <Icon size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{step.desc}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      {steps.map((_, i) => (
                        <div key={i} className={`w-8 h-1.5 rounded-full transition ${i < onboardingStep ? 'bg-brand-blue' : 'bg-gray-200'}`}></div>
                      ))}
                    </div>
                    <button onClick={() => {
                      if (onboardingStep < 5) { setOnboardingStep(onboardingStep + 1); }
                      else { setShowWelcome(false); localStorage.setItem('hasSeenWelcome', 'true'); }
                    }} className="bg-brand-blue text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition text-sm">
                      {onboardingStep < 5 ? 'Next' : 'Get Started'}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Celebration Overlay */}
      {showCelebration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-testid="celebration-overlay">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-2xl text-center animate-bounce-in pointer-events-auto">
            <div className="text-6xl mb-4">
              <Award size={64} className="text-yellow-500 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-brand-blue mb-2">First Report Submitted!</h2>
            <p className="text-gray-600">Great work! You're on your way to building strong habits.</p>
          </div>
        </div>
      )}

      {/* Save indicator */}
      {(isSaving || lastSaved) && (
        <div className="print:hidden">
          <div className="flex justify-end text-xs text-gray-400 mb-1">
            {isSaving && (
              <span className="flex items-center gap-1">
                <Save size={12} className="animate-pulse" />
                Saving...
              </span>
            )}
            {lastSaved && !isSaving && (
              <span>Saved {lastSaved.toLocaleTimeString()}</span>
            )}
          </div>
        </div>
      )}

      <div>
        {/* Compact Header: Welcome + Week Nav + Stats */}
        <div className="mb-3 bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-3" data-testid="quick-stats-bar">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            {/* Left: Welcome + Week Navigation */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handlePreviousWeek}
                className="min-w-[36px] min-h-[36px] flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-md transition"
                data-testid="prev-week-btn"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="text-center">
                <div className="flex items-center gap-2 flex-wrap">
                  <Calendar size={16} className="text-brand-blue" />
                  <span className="text-sm font-bold text-brand-blue" data-testid="week-date-range">
                    {formatDateDisplay(currentWeekStart)} - {formatDateDisplay(currentWeekEnd)}
                  </span>
                  {isSubmitted && (
                    <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1" data-testid="status-submitted">
                      <Check size={12} /> Submitted
                    </span>
                  )}
                  {!isSubmitted && (
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-semibold" data-testid="status-draft">
                      Draft
                    </span>
                  )}
                  {isThisWeek && (
                    <span className="text-xs text-brand-orange font-semibold" data-testid="current-week-label">Current Week</span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  Welcome back, {agentInfo?.fullName?.split(' ')[0]} - Data auto-saves
                </p>
              </div>
              <button
                onClick={handleNextWeek}
                className="min-w-[36px] min-h-[36px] flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-md transition"
                data-testid="next-week-btn"
              >
                <ChevronRight size={18} />
              </button>
              {!isThisWeek && (
                <button
                  onClick={handleThisWeek}
                  className="px-3 py-1.5 bg-brand-orange text-white hover:bg-brand-orange-light rounded-md transition font-semibold text-xs"
                  data-testid="this-week-btn"
                >
                  This Week
                </button>
              )}
              <button
                onClick={() => setShowCalendar(!showCalendar)}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition text-xs font-medium flex items-center gap-1"
                data-testid="toggle-calendar-btn"
              >
                <Calendar size={12} />
                {showCalendar ? 'Hide' : 'Calendar'}
              </button>
            </div>

            {/* Right: Quick Stats */}
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="text-center">
                <div className="text-lg font-bold text-brand-blue">{totals?.sales || 0}</div>
                <div className="text-[10px] text-gray-500 font-medium">Sales</div>
              </div>
              <div className="w-px h-6 bg-gray-200 hidden sm:block"></div>
              <div className="text-center">
                <div className="text-lg font-bold text-brand-orange">{totals?.presentations || 0}</div>
                <div className="text-[10px] text-gray-500 font-medium">Pres.</div>
              </div>
              <div className="w-px h-6 bg-gray-200 hidden sm:block"></div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">${totals?.annualizedPremium?.toFixed(0) || 0}</div>
                <div className="text-[10px] text-gray-500 font-medium">Premium</div>
              </div>
              <div className="w-px h-6 bg-gray-200 hidden sm:block"></div>
              <div className="text-center">
                <div className={`text-lg font-bold ${isSubmitted ? 'text-green-600' : 'text-yellow-500'}`}>
                  {isSubmitted ? <Check size={18} className="mx-auto" /> : <span className="text-xs">Draft</span>}
                </div>
                <div className="text-[10px] text-gray-500 font-medium">Status</div>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Calendar + Info Block + Reminder */}
        {showCalendar && (
          <div className="mb-3 flex gap-3 items-start" data-testid="calendar-container">
            <WeekCalendar
              weekStart={currentWeekStart}
              weekEnd={currentWeekEnd}
              onWeekSelect={handleCalendarWeekSelect}
            />
            <ReportInfoBlock
              agentName={agentInfo?.fullName || 'Agent'}
              dateLabel="Week of"
              dateValue={`${formatDateDisplay(currentWeekStart)} - ${formatDateDisplay(currentWeekEnd)}`}
              goalLabel="Weekly AP Goal"
              goalValue={reportMeta.weeklyApGoal}
              onGoalChange={(v) => { setReportMeta(prev => ({ ...prev, weeklyApGoal: v })); }}
              areasWorked={reportMeta.areasWorked}
              onAreasWorkedChange={(v) => { setReportMeta(prev => ({ ...prev, areasWorked: v })); }}
            />
            <div className="hidden lg:flex flex-col bg-white rounded-lg shadow-md p-4 min-w-[260px] ml-auto" data-testid="reminder-box">
              <h3 className="text-sm font-bold text-brand-blue mb-3 flex items-center gap-1.5">
                <Info size={14} /> Reminders
              </h3>
              <ul className="space-y-3 text-xs text-gray-600 flex-1">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                  <span>Data <strong>auto-saves</strong> as you type</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-orange mt-1.5 flex-shrink-0" />
                  <span>Reports auto-send every <strong>Sunday 12 PM ET</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-blue mt-1.5 flex-shrink-0" />
                  <span>Set up your weekly activity defaults in the <a href="/agent/settings" className="text-brand-blue font-semibold hover:underline">Settings</a> tab to pre-fill forms each week</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 flex-shrink-0" />
                  <span>Use <strong>Send Report</strong> to manually email your report to your trainer</span>
                </li>
              </ul>
              <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                Status: <span className={`font-semibold ${isSubmitted ? 'text-green-600' : 'text-yellow-600'}`}>{isSubmitted ? 'Submitted' : 'Draft'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Activity Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-3" data-testid="activity-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b-2 border-gray-300">
                  <th className="px-2 py-2 text-center font-bold text-xs">Off</th>
                  <th className="px-2 py-2 text-center font-bold text-xs">Day</th>
                  <th className="px-2 py-2 text-center font-bold text-xs">Start<br/>Time</th>
                  <th className="px-2 py-2 text-center font-bold text-xs">End<br/>Time</th>
                  <th className="px-2 py-2 text-center font-bold text-xs"># of Leads<br/>Worked</th>
                  <th className="px-2 py-2 text-center font-bold text-xs"># of Door<br/>Knocks</th>
                  <th className="px-2 py-2 text-center font-bold text-xs"># Phone<br/>Calls</th>
                  <th className="px-2 py-2 text-center font-bold text-xs">Presentations<br/>Made</th>
                  <th className="px-2 py-2 text-center font-bold text-xs">Total #<br/>of Sales</th>
                  <th className="px-2 py-2 text-center font-bold text-xs">Future<br/>Appts Set</th>
                  <th className="px-2 py-2 text-center font-bold text-xs">Annual<br/>Premium<br/>Collected</th>
                  <th className="px-2 py-2 text-center font-bold text-xs">Referrals<br/>Received</th>
                  <th className="px-2 py-2 text-center font-bold text-xs">Referrals<br/>Contacted</th>
                  <th className="px-1 py-2 text-center font-bold text-xs w-16"></th>
                </tr>
              </thead>
              <tbody>
                {activityData.activities.map((day, index) => (
                  <tr
                    key={index}
                    className={`border-b hover:bg-gray-50 ${day.offDay ? 'bg-gray-100 opacity-60' : ''}`}
                    data-testid={`activity-row-${day.dayOfWeek?.toLowerCase()}`}
                  >
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" checked={day.offDay} onChange={(e) => updateActivity(index, 'offDay', e.target.checked)} className="w-4 h-4 cursor-pointer" data-testid={`offday-${index}`} />
                    </td>
                    <td className="px-2 py-1.5 font-semibold whitespace-nowrap text-xs">
                      {day.dayOfWeek}
                      <span className="text-gray-400 font-normal ml-1">{formatDayWithDate(day.date)}</span>
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="time" value={day.startTime} onChange={(e) => updateActivity(index, 'startTime', e.target.value)} disabled={day.offDay} className="w-32 px-2 py-1 border rounded text-center text-sm disabled:bg-gray-100" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="time" value={day.endTime} onChange={(e) => updateActivity(index, 'endTime', e.target.value)} disabled={day.offDay} className="w-32 px-2 py-1 border rounded text-center text-sm disabled:bg-gray-100" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={day.leadsWorked} onChange={(e) => updateActivity(index, 'leadsWorked', parseInt(e.target.value) || 0)} disabled={day.offDay} className="w-16 px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100" min="0" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={day.doorKnocks} onChange={(e) => updateActivity(index, 'doorKnocks', parseInt(e.target.value) || 0)} disabled={day.offDay} className="w-16 px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100" min="0" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={day.phoneCalls} onChange={(e) => updateActivity(index, 'phoneCalls', parseInt(e.target.value) || 0)} disabled={day.offDay} className="w-16 px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100" min="0" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={day.presentations} onChange={(e) => updateActivity(index, 'presentations', parseInt(e.target.value) || 0)} disabled={day.offDay} className="w-16 px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100" min="0" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={day.sales} onChange={(e) => updateActivity(index, 'sales', parseInt(e.target.value) || 0)} disabled={day.offDay} className="w-16 px-1 py-1 border rounded text-center text-sm bg-green-50 disabled:bg-gray-100" min="0" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={day.futureAppointments} onChange={(e) => updateActivity(index, 'futureAppointments', parseInt(e.target.value) || 0)} disabled={day.offDay} className="w-16 px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100" min="0" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" step="0.01" value={day.annualizedPremium} onChange={(e) => updateActivity(index, 'annualizedPremium', parseFloat(e.target.value) || 0)} disabled={day.offDay} className="w-20 px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100" min="0" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={day.referralsReceived} onChange={(e) => updateActivity(index, 'referralsReceived', parseInt(e.target.value) || 0)} disabled={day.offDay} className="w-16 px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100" min="0" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={day.referralsContacted} onChange={(e) => updateActivity(index, 'referralsContacted', parseInt(e.target.value) || 0)} disabled={day.offDay} className="w-16 px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100" min="0" />
                    </td>
                    <td className="px-1 py-1.5 text-center">
                      {!day.offDay && dayHasData(day) && (
                        dailySentDates.includes(day.date) ? (
                          <span className="inline-flex items-center gap-0.5 text-green-600 text-xs font-semibold" data-testid={`day-sent-${index}`}>
                            <Check size={12} /> Sent
                          </span>
                        ) : (
                          <button
                            onClick={() => handleSendDailyReport(index)}
                            disabled={sendingDay === index}
                            className="inline-flex items-center gap-0.5 px-2 py-1 bg-brand-blue text-white rounded text-xs font-semibold hover:bg-brand-orange transition disabled:opacity-50"
                            data-testid={`send-day-${index}`}
                          >
                            {sendingDay === index ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                            {sendingDay !== index && ' Send'}
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}

                {/* Totals Row */}
                {totals && (
                  <tr className="bg-brand-blue text-white font-bold" data-testid="totals-row">
                    <td colSpan="2" className="px-2 py-2 text-right text-xs">TOTALS:</td>
                    <td colSpan="2" className="px-2 py-2"></td>
                    <td className="px-2 py-2 text-center text-xs">{totals.leadsWorked}</td>
                    <td className="px-2 py-2 text-center text-xs">{totals.doorKnocks}</td>
                    <td className="px-2 py-2 text-center text-xs">{totals.phoneCalls}</td>
                    <td className="px-2 py-2 text-center text-xs">{totals.presentations}</td>
                    <td className="px-2 py-2 text-center text-xs bg-green-600">{totals.sales}</td>
                    <td className="px-2 py-2 text-center text-xs">{totals.futureAppointments}</td>
                    <td className="px-2 py-2 text-center text-xs">${totals.annualizedPremium.toFixed(2)}</td>
                    <td className="px-2 py-2 text-center text-xs">{totals.referralsReceived}</td>
                    <td className="px-2 py-2 text-center text-xs">{totals.referralsContacted}</td>
                    <td className="px-1 py-2"></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes + Action Buttons */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-3">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs font-bold text-gray-700 mb-1">Notes</label>
              <textarea
                value={activityData.notes}
                onChange={(e) => {
                  setActivityData({ ...activityData, notes: e.target.value });
                  triggerAutosave();
                }}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm"
                placeholder="Any additional notes about this week..."
                data-testid="notes-textarea"
              />
            </div>
            <div className="flex-shrink-0 flex gap-2">
              <button
                onClick={() => setShowSendModal(true)}
                className="flex items-center gap-2 bg-green-500 text-white px-4 py-2.5 rounded-md hover:bg-green-600 transition font-semibold text-sm min-h-[44px]"
                data-testid="open-send-report-btn"
              >
                <Mail size={18} /> Send Report
              </button>
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 bg-brand-blue text-white px-6 py-2.5 rounded-md hover:bg-brand-orange transition font-semibold text-sm min-h-[44px]"
                data-testid="submit-report-btn"
              >
                <Check size={18} />
                {isSubmitted ? 'Save Report' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>

      <SendReportModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        reportType="activity"
        weekStartDate={currentWeekStart}
      />
    </AgentLayout>
  );
};

export default AgentDashboard;
